<script setup lang="ts">
    import SvgIcon from '@/js/components/SvgIcon.vue';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { channelNameTranslations } from '@/js/services/api/interaction/consts';
    import { HourglassIcon, UserRoundPenIcon } from '@lucide/vue';
    import dayjs from 'dayjs';
    import { isArray } from 'highcharts';
    import CoachingCategoryLabel from '../coaching/CoachingCategoryLabel.vue';
    import { isNullOrUndefinedOrEmpty } from '@/js/utils/isNullOrUndefined';
    import { secondsToHHMMSS } from '@/js/utils/dateUtils';

    const props = defineProps<{
        field: string;
        data:
            | string
            | number
            | Date
            | string[]
            | boolean
            | null
            | {
                  minPoints: number;
                  maxPoints: number;
                  gotPoints: number;
                  isNotApplicable?: boolean;
              };
        showAsPercentage?: boolean;
        showHeatMap?: boolean;
        averageData?: { minPoints: number; maxPoints: number; gotPoints: number };
        editedByHuman?: boolean;
    }>();

    function isSentimentField(field: string) {
        return (
            field === 'sentiment' ||
            field === 'agent-sentiment' ||
            field === 'customer-sentiment' ||
            field === 'agent-sentiment-level-names' ||
            field === 'customer-sentiment-level-names'
        );
    }

    function isSentimentDirectionField(field: string) {
        return (
            field === 'agent-direction' ||
            field === 'customer-direction' ||
            field === 'agent-sentiment-direction' ||
            field === 'customer-sentiment-direction'
        );
    }

    function isSentimentScorePercentageField(field: string) {
        return field.endsWith('sentiment-score-percentage');
    }

    function getDirection(direction: string) {
        switch (direction) {
            case '-1':
                return 'down';
            case '−1':
                return 'down';
            case '0':
                return 'neutral';
            case '1':
                return 'up';
            default:
                return '';
        }
    }

    function getProtocolResults(
        data: {
            minPoints: number;
            maxPoints: number;
            gotPoints: number;
            isNotApplicable?: boolean;
        } | null
    ) {
        if (!data) {
            return '';
        }

        if (data.isNotApplicable) {
            return props.showAsPercentage ? '' : __('not-applicable-short');
        }

        const percentage = data.maxPoints === 0 ? 0 : (data.gotPoints / data.maxPoints) * 100;

        return props.showAsPercentage
            ? `${formatNumber(percentage, 1)}%`
            : `${formatNumber(data.gotPoints, 1)} / ${formatNumber(data.maxPoints, 1)}`;
    }

    function pct(x: { maxPoints: number; gotPoints: number } | null) {
        if (!x) {
            return null;
        }

        return x.maxPoints === 0 ? 0 : (x.gotPoints / x.maxPoints) * 100;
    }

    function formatRecordingLength(seconds: number) {
        if (!Number.isFinite(seconds) || seconds <= 0) {
            return '-';
        }
        return secondsToHHMMSS(Math.floor(seconds));
    }

    function formatNumber(value: number, decimals = 1) {
        const roundedValue = Number(value.toFixed(decimals));
        const roundedInt = Math.round(roundedValue);
        const FORMAT_EPSILON = 0.000001;
        if (Math.abs(roundedValue - roundedInt) < FORMAT_EPSILON) {
            return roundedInt.toString();
        }

        return roundedValue.toFixed(decimals);
    }

    function getHeatMapColor(
        cell: { minPoints: number; maxPoints: number; gotPoints: number } | null
    ) {
        const avg = props.averageData;
        if (!avg || !cell) {
            return '';
        }

        const cellPct = pct(cell);
        const avgPct = pct(avg);

        if (cellPct === null || avgPct === null) {
            return '';
        }

        const diff = avgPct - cellPct;

        if (diff <= 0) {
            return 'bg-sentiment-positive/40';
        }
        if (diff <= 5) {
            return 'bg-sentiment-positive-to-neutral/40';
        }
        if (diff <= 15) {
            return 'bg-sentiment-neutral-to-positive/40';
        }
        if (diff <= 25) {
            return 'bg-sentiment-neutral/40';
        }
        if (diff <= 30) {
            return 'bg-sentiment-neutral-to-negative/40';
        }
        if (diff <= 40) {
            return 'bg-sentiment-negative-to-neutral/40';
        }
        return 'bg-sentiment-negative/40';
    }
</script>
<template>
    <div class="w-full h-full flex justify-start items-center text-start text-body bg-primary-gradient">
        <template
            v-if="
                field === 'ProtocolResults' ||
                field === 'recording-length' ||
                !isNullOrUndefinedOrEmpty(data)
            "
        >
            <template
                v-if="isSentimentField(field) && isArray(data) && (data as string[])[0].length > 0"
            >
                <SvgIcon
                    :name="'sentiment-' + (data as string[])[0]?.toLowerCase() + '.svg'"
                    size="size-7"
                    class="fill-transparent stroke-transparent"
                />
            </template>
            <template v-else-if="['customer-sentiment', 'agent-sentiment'].includes(field)">
                <p class="text-caption">{{ data }}{{ __('percentage-character') }}</p>
            </template>
            <template v-else-if="field === 'tracker-names' || field === 'skill-names'">
                <p class="text-caption">
                    {{ (data as string[]).join(' ') }}
                </p>
            </template>
            <template v-else-if="isSentimentDirectionField(field) && typeof data === 'string'">
                <SvgIcon
                    :name="'sentimentdirection-' + getDirection(data as string) + '.svg'"
                    size="size-8"
                    class="fill-transparent stroke-transparent"
                />
            </template>
            <template
                v-else-if="
                    isSentimentScorePercentageField(field) &&
                    (typeof data === 'string' || typeof data === 'number')
                "
            >
                <p class="text-caption">{{ `${data}${__('percentage-character')}` }}</p>
            </template>
            <template v-else-if="field === 'coached-at' || field === 'last-updated-at'">
                <p class="text-caption">
                    {{ dayjs(data as string).format('YYYY-MM-DD HH:mm') }}
                </p>
            </template>
            <template v-else-if="field === 'pending-approval'">
                <HourglassIcon v-if="data === true" class="size-5" />
            </template>
            <template
                v-else-if="
                    field === 'submitted-by-me' ||
                    field === 'action-required' ||
                    field === 'coached'
                "
            >
                <p class="text-caption">
                    {{ !data ? __('no') : __('yes') }}
                </p>
            </template>
            <template v-else-if="field === 'created-by-ai'">
                <CoachingCategoryLabel :ai="!!data">
                    <p class="whitespace-nowrap flex items-center gap-1">
                        {{ !!data ? __('ai') : __('manual') }}
                        <UserRoundPenIcon v-if="data && editedByHuman" class="size-3" />
                    </p>
                </CoachingCategoryLabel>
            </template>
            <template v-else-if="field === 'ProtocolResults'">
                <template v-if="showHeatMap && data !== null">
                    <div
                        class="absolute p-3 inset-0 flex justify-start items-center text-start"
                        :class="getHeatMapColor(data as any)"
                    >
                        <p class="text-caption">
                            {{ getProtocolResults(data as any) }}
                        </p>
                    </div>
                </template>
                <template v-else>
                    <p class="text-caption">
                        {{ getProtocolResults(data as any) }}
                    </p>
                </template>
            </template>
            <template v-else-if="field === 'ai-metadata-resolved-rate'">
                <p v-if="data === '100 %'" class="text-caption">{{ __('yes') }}</p>
                <p v-else class="text-caption">{{ __('no') }}</p>
            </template>
            <template v-else-if="field === 'recording-length'">
                <p class="text-caption">
                    {{ formatRecordingLength(Number(data)) }}
                </p>
            </template>
            <template v-else-if="field === 'agent-has-read' || field === 'agent-has-commented'">
                <p class="text-caption">
                    {{ data === true || data === 'true' ? __('yes') : __('no') }}
                </p>
            </template>
            <template v-else-if="field === 'channel'">
                <p class="text-caption">
                    {{
                        channelNameTranslations[data as keyof typeof channelNameTranslations]
                            ? __untypedExpression(
                                  channelNameTranslations[
                                      data as keyof typeof channelNameTranslations
                                  ]
                              )
                            : data
                    }}
                </p>
            </template>
            <template v-else>
                <p class="text-caption">{{ data }}</p>
            </template>
        </template>
        <template v-else>
            <!-- Empty response for column -->
        </template>
    </div>
</template>
